import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Warehouse, Plus, Edit2, Trash2, FileText, DollarSign,
  MapPin, Ruler, Landmark, XCircle, FileSignature, Eye,
  RefreshCw, AlertTriangle, CheckCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import LeaseModal from './LeaseModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const LOGO_URL = `${API_BASE}/uploads/logos/logo_20260410_145221.PNG`;

const EMPTY_FORM = {
  name: '', location: '', surface_sqft: '', cadastral_value: '',
  monthly_rent: '', amenities: '', status: 'available', is_owned_by_airport: true,
};

const EMPTY_LEASE_EDIT = {
  monthly_rent: '', end_date: '', payment_day: 1, is_auto_renew: false, notes: '',
};

const HangarsView = () => {
  const [hangars, setHangars]         = useState([]);
  const [leases, setLeases]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [activeTab, setActiveTab]     = useState('hangars');

  // Hangar modal
  const [showModal, setShowModal]     = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData]       = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);

  // Lease create modal
  const [showLeaseModal, setShowLeaseModal]               = useState(false);
  const [selectedHangarForLease, setSelectedHangarForLease] = useState(null);

  // Lease edit modal
  const [showLeaseEditModal, setShowLeaseEditModal]       = useState(false);
  const [editingLease, setEditingLease]                   = useState(null);
  const [leaseEditForm, setLeaseEditForm]                 = useState(EMPTY_LEASE_EDIT);
  const [savingLease, setSavingLease]                     = useState(false);

  // Print lease modal
  const [showPrintModal, setShowPrintModal]               = useState(false);
  const [printLease, setPrintLease]                       = useState(null);

  // Invoice preview modal
  const [showInvoiceModal, setShowInvoiceModal]           = useState(false);
  const [leaseInvoices, setLeaseInvoices]                 = useState([]);
  const [loadingInvoices, setLoadingInvoices]             = useState(false);
  const [selectedLeaseForInvoice, setSelectedLeaseForInvoice] = useState(null);
  
  // Mobile: expanded lease row
  const [expandedLeaseId, setExpandedLeaseId] = useState(null);

  /* ── Fetch ──────────────────────────────────────────────── */
  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const [hangarsRes, leasesRes] = await Promise.all([
        axios.get(`${API_BASE}/hangars/`),
        axios.get(`${API_BASE}/leases/active`),
      ]);
      setHangars(hangarsRes.data);
      setLeases(leasesRes.data);
    } catch (err) {
      setError('Unable to load data. Please check that the server is running.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  /* ── CRUD Hangar ────────────────────────────────────────── */
  const handleHangarSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        surface_sqft:    formData.surface_sqft    ? parseFloat(formData.surface_sqft)    : undefined,
        cadastral_value: formData.cadastral_value ? parseFloat(formData.cadastral_value) : undefined,
        monthly_rent:    formData.monthly_rent    ? parseFloat(formData.monthly_rent)    : 0,
      };
      if (editingItem) {
        await axios.put(`${API_BASE}/hangars/${editingItem.id}`, payload);
      } else {
        await axios.post(`${API_BASE}/hangars/`, payload);
      }
      await fetchData();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving data');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHangar = async (hangar) => {
    if (!window.confirm(`Delete hangar ${hangar.hangar_number}?`)) return;
    try {
      await axios.delete(`${API_BASE}/hangars/${hangar.id}`);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting hangar');
    }
  };

  /* ── CRUD Lease ─────────────────────────────────────────── */
  const handleTerminateLease = async (lease) => {
    if (!window.confirm(`Terminate lease ${lease.lease_number}?\nThe hangar will become available.`)) return;
    try {
      await axios.post(`${API_BASE}/leases/${lease.id}/terminate`);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error terminating lease');
    }
  };

  const openLeaseEdit = (lease) => {
    setEditingLease(lease);
    setLeaseEditForm({
      monthly_rent:  lease.monthly_rent  ?? '',
      end_date:      lease.end_date      ?? '',
      payment_day:   lease.payment_day   ?? 1,
      is_auto_renew: lease.is_auto_renew ?? false,
      notes:         lease.notes         ?? '',
    });
    setShowLeaseEditModal(true);
  };

  const handleLeaseEditSubmit = async () => {
    setSavingLease(true);
    try {
      await axios.put(`${API_BASE}/leases/${editingLease.id}`, {
        monthly_rent:  parseFloat(leaseEditForm.monthly_rent) || editingLease.monthly_rent,
        end_date:      leaseEditForm.end_date || null,
        payment_day:   parseInt(leaseEditForm.payment_day)   || 1,
        is_auto_renew: leaseEditForm.is_auto_renew,
        notes:         leaseEditForm.notes,
      });
      await fetchData();
      setShowLeaseEditModal(false);
      setEditingLease(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating lease');
    } finally {
      setSavingLease(false);
    }
  };

  /* ── Print lease ───────────────────────────────────────── */
  const openPrintLease = async (lease) => {
    try {
      const res = await axios.get(`${API_BASE}/leases/${lease.id}`);
      setPrintLease(res.data);
    } catch {
      setPrintLease(lease);
    }
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const content = document.getElementById('lease-print-content').innerHTML;
    printWindow.document.write(`
      <html><head><title>Lease ${printLease?.lease_number}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; color: #000; }
        @media print { body { padding: 20px; } }
      </style></head><body>${content}</body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 400);
  };

  /* ── Invoices for lease ─────────────────────────────────── */
  const openLeaseInvoices = async (lease) => {
    setSelectedLeaseForInvoice(lease);
    setShowInvoiceModal(true);
    setLoadingInvoices(true);
    try {
      const res = await axios.get(`${API_BASE}/invoices/by-lease/${lease.id}`);
      setLeaseInvoices(res.data);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setLeaseInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleGenerateInvoice = async (leaseId) => {
    try {
      await axios.post(`${API_BASE}/invoices/generate-from-lease`, {
        lease_id: leaseId,
        month: new Date().toISOString().slice(0, 7),
      });
      await openLeaseInvoices(selectedLeaseForInvoice);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error generating invoice');
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice as paid?')) return;
    try {
      await axios.put(`${API_BASE}/invoices/${invoiceId}/pay`);
      await openLeaseInvoices(selectedLeaseForInvoice);
    } catch (err) {
      alert('Error processing payment');
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const res = await axios.get(`${API_BASE}/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Error downloading PDF');
    }
  };

  /* ── Modal helpers ──────────────────────────────────────── */
  const openCreateModal = () => { setEditingItem(null); setFormData(EMPTY_FORM); setShowModal(true); };
  const openEditModal = (hangar) => {
    setEditingItem(hangar);
    setFormData({
      name: hangar.name ?? '', location: hangar.location ?? '',
      surface_sqft: hangar.surface_sqft ?? '', cadastral_value: hangar.cadastral_value ?? '',
      monthly_rent: hangar.monthly_rent ?? '', amenities: hangar.amenities ?? '',
      status: hangar.status ?? 'available', is_owned_by_airport: hangar.is_owned_by_airport ?? true,
    });
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditingItem(null); setFormData(EMPTY_FORM); };
  const field = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  /* ── UI helpers ─────────────────────────────────────────── */
  const statusStyle = (s) => {
    switch (s) {
      case 'available':   return 'text-green-400 bg-green-500/20';
      case 'occupied':    return 'text-blue-400 bg-blue-500/20';
      case 'maintenance': return 'text-yellow-400 bg-yellow-500/20';
      default:            return 'text-gray-400 bg-gray-500/20';
    }
  };
  const statusLabel = (s) => ({ available: 'Available', occupied: 'Occupied', maintenance: 'Maintenance' }[s] || s);

  const invoiceStatusStyle = (s) => {
    switch (s) {
      case 'paid':    return 'text-green-400 bg-green-500/20';
      case 'pending': return 'text-yellow-400 bg-yellow-500/20';
      case 'overdue': return 'text-red-400 bg-red-500/20';
      default:        return 'text-gray-400 bg-gray-500/20';
    }
  };
  const invoiceStatusLabel = (s) => ({ paid: 'Paid', pending: 'Pending', overdue: 'Overdue' }[s] || s);

  const getLeaseTypeLabel = (type) => {
    switch (type) {
      case 'active': return { label: 'Active Lease', color: 'bg-blue-500/20 text-blue-400' };
      case 'ground': return { label: 'Ground Lease', color: 'bg-green-500/20 text-green-400' };
      case 'services_only': return { label: 'Services Only', color: 'bg-purple-500/20 text-purple-400' };
      default: return { label: 'Active Lease', color: 'bg-blue-500/20 text-blue-400' };
    }
  };

  /* ── Render ─────────────────────────────────────────────── */
  if (loading) return (
    <div className="flex items-center justify-center py-20 text-gray-400">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3" /> Loading…
    </div>
  );
  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 px-4">
      <p className="text-red-400 text-center">{error}</p>
      <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Retry</button>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6 px-2 sm:px-0">

      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white">Hangar & Lease Management</h2>
          <p className="text-gray-400 text-xs md:text-sm mt-1">Manage hangars, leases, and tenant agreements</p>
        </div>
        <button onClick={openCreateModal}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 transition text-sm sm:text-base w-full sm:w-auto justify-center">
          <Plus size={18} /> New Hangar
        </button>
      </div>

      {/* Tabs - Scrollable on mobile */}
      <div className="flex gap-2 border-b border-gray-700 overflow-x-auto whitespace-nowrap">
        {[
          { key: 'hangars', label: `Hangars (${hangars.length})`, Icon: Warehouse },
          { key: 'leases',  label: `Active Leases (${leases.length})`, Icon: FileText },
        ].map(({ key, label, Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition flex items-center gap-1 sm:gap-2
              ${activeTab === key ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}>
            <Icon size={14} className="sm:w-4 sm:h-4" /> {label}
          </button>
        ))}
      </div>

      {/* ── Hangars tab ── Responsive grid */}
      {activeTab === 'hangars' && (
        hangars.length === 0
          ? <p className="text-center text-gray-500 py-16">No hangars registered.</p>
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
              {hangars.map((h) => (
                <div key={h.id} className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 md:p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-bold text-base md:text-lg">{h.hangar_number}</p>
                      {h.name && <p className="text-gray-400 text-xs md:text-sm">{h.name}</p>}
                    </div>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusStyle(h.status)}`}>
                      {statusLabel(h.status)}
                    </span>
                  </div>
                  <div className="space-y-1 text-xs md:text-sm text-gray-400">
                    {h.location && <p className="flex items-center gap-1"><MapPin size={12} className="md:w-3 md:h-3"/> {h.location}</p>}
                    {h.surface_sqft && <p className="flex items-center gap-1"><Ruler size={12} className="md:w-3 md:h-3"/> {Number(h.surface_sqft).toLocaleString()} sq ft</p>}
                    {h.monthly_rent > 0 && <p className="flex items-center gap-1 text-green-400"><DollarSign size={12} className="md:w-3 md:h-3"/> ${Number(h.monthly_rent).toLocaleString()}/month</p>}
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t border-gray-700">
                    <button onClick={() => openEditModal(h)} className="text-blue-400 hover:text-blue-300 transition p-1" title="Edit"><Edit2 size={14} className="md:w-4 md:h-4"/></button>
                    {h.status === 'available' && (
                      <button onClick={() => { setSelectedHangarForLease(h); setShowLeaseModal(true); }}
                        className="text-green-400 hover:text-green-300 transition p-1" title="Create lease">
                        <FileSignature size={14} className="md:w-4 md:h-4"/>
                      </button>
                    )}
                    <button onClick={() => handleDeleteHangar(h)} className="text-red-400 hover:text-red-300 transition p-1" title="Delete"><Trash2 size={14} className="md:w-4 md:h-4"/></button>
                  </div>
                </div>
              ))}
            </div>
          )
      )}

      {/* ── Leases tab ── Responsive: Tableau sur desktop, cartes sur mobile */}
      {activeTab === 'leases' && (
        leases.length === 0
          ? <p className="text-center text-gray-500 py-16">No active leases.</p>
          : (
            <>
              {/* Version Desktop - Tableau */}
              <div className="hidden md:block bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-800 border-b border-gray-700">
                      <tr>
                        {['Lease #', 'Hangar', 'Tenant', 'Type', 'Monthly Rent', 'Deposit', 'Period', 'Status', 'Actions'].map(h => (
                          <th key={h} className="p-3 text-left text-gray-400 text-sm font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {leases.map((l) => {
                        const leaseTypeInfo = getLeaseTypeLabel(l.lease_type);
                        return (
                          <tr key={l.id} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition">
                            <td className="p-3 text-white text-sm font-mono">{l.lease_number}</td>
                            <td className="p-3 text-gray-300 text-sm">{l.hangar_number || '—'}</td>
                            <td className="p-3 text-gray-300 text-sm">{l.tenant_name || '—'}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-lg text-xs font-medium ${leaseTypeInfo.color}`}>
                                {leaseTypeInfo.label}
                              </span>
                            </td>
                            <td className="p-3 text-green-400 text-sm font-semibold">
                              ${Number(l.monthly_rent || l.ground_rent || 0).toLocaleString()}
                            </td>
                            <td className="p-3 text-gray-400 text-sm">${Number(l.security_deposit || 0).toLocaleString()}</td>
                            <td className="p-3 text-gray-300 text-sm">
                              {new Date(l.start_date).toLocaleDateString('en-US')}
                              {l.end_date ? ` → ${new Date(l.end_date).toLocaleDateString('en-US')}` : ' → ∞'}
                            </td>
                            <td className="p-3">
                              <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-lg text-xs font-medium">Active</span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <button onClick={() => openPrintLease(l)} className="p-1.5 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-400 transition" title="View and print lease"><Eye size={14}/></button>
                                <button onClick={() => openLeaseInvoices(l)} className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition" title="View invoices / receipts"><FileText size={14}/></button>
                                <button onClick={() => openLeaseEdit(l)} className="p-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-lg text-yellow-400 transition" title="Edit lease"><Edit2 size={14}/></button>
                                <button onClick={() => handleTerminateLease(l)} className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition" title="Terminate lease"><XCircle size={14}/></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Version Mobile - Cartes */}
              <div className="md:hidden space-y-3">
                {leases.map((l) => {
                  const leaseTypeInfo = getLeaseTypeLabel(l.lease_type);
                  const isExpanded = expandedLeaseId === l.id;
                  return (
                    <div key={l.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-bold text-sm">{l.lease_number}</p>
                          <p className="text-gray-400 text-xs">{l.hangar_number || '—'} • {l.tenant_name || '—'}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${leaseTypeInfo.color}`}>
                          {leaseTypeInfo.label}
                        </span>
                      </div>
                      
                      <div className="mt-2 space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Monthly Rent:</span>
                          <span className="text-green-400 font-semibold">${Number(l.monthly_rent || l.ground_rent || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Deposit:</span>
                          <span className="text-gray-300">${Number(l.security_deposit || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Period:</span>
                          <span className="text-gray-300 text-xs">
                            {new Date(l.start_date).toLocaleDateString()}
                            {l.end_date ? ` → ${new Date(l.end_date).toLocaleDateString()}` : ' → ∞'}
                          </span>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => openPrintLease(l)} className="flex-1 py-1.5 bg-purple-500/20 rounded-lg text-purple-400 text-xs flex items-center justify-center gap-1"><Eye size={12}/> Print</button>
                            <button onClick={() => openLeaseInvoices(l)} className="flex-1 py-1.5 bg-blue-500/20 rounded-lg text-blue-400 text-xs flex items-center justify-center gap-1"><FileText size={12}/> Invoices</button>
                            <button onClick={() => openLeaseEdit(l)} className="flex-1 py-1.5 bg-yellow-500/20 rounded-lg text-yellow-400 text-xs flex items-center justify-center gap-1"><Edit2 size={12}/> Edit</button>
                            <button onClick={() => handleTerminateLease(l)} className="flex-1 py-1.5 bg-red-500/20 rounded-lg text-red-400 text-xs flex items-center justify-center gap-1"><XCircle size={12}/> Terminate</button>
                          </div>
                        </div>
                      )}
                      
                      <button 
                        onClick={() => setExpandedLeaseId(isExpanded ? null : l.id)}
                        className="w-full mt-2 flex justify-center items-center gap-1 text-gray-500 text-xs"
                      >
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {isExpanded ? 'Show less' : 'Show actions'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )
      )}

      {/* ══════════════════════════════════════════════
          Modal — Edit Hangar - Responsive
      ══════════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-700">
              <h3 className="text-lg sm:text-xl font-bold text-white">
                {editingItem ? `Edit ${editingItem.hangar_number}` : 'New Hangar'}
              </h3>
              {!editingItem && <p className="text-gray-400 text-xs mt-1 hidden sm:block">The number (H-01, H-02…) will be assigned automatically.</p>}
            </div>
            <div className="p-4 sm:p-6 space-y-3 max-h-[70vh] overflow-y-auto">
              {[
                { label: 'Name', key: 'name', placeholder: 'ex: North Hangar' },
                { label: 'Location', key: 'location', placeholder: 'ex: North Ramp' },
              ].map(({ label, key, placeholder }) => (
                <div key={key}>
                  <label className="text-gray-400 text-xs mb-1 block">{label}</label>
                  <input type="text" placeholder={placeholder} value={formData[key]}
                    onChange={e => field(key, e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"/>
                </div>
              ))}
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Surface (sq ft) *</label>
                <input type="number" min="0" placeholder="ex: 2500" value={formData.surface_sqft}
                  onChange={e => field('surface_sqft', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"/>
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Cadastral Value ($)</label>
                <input type="number" min="0" value={formData.cadastral_value}
                  onChange={e => field('cadastral_value', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"/>
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Monthly Rent ($)</label>
                <input type="number" min="0" value={formData.monthly_rent}
                  onChange={e => field('monthly_rent', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"/>
              </div>
              {editingItem && (
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Status</label>
                  <select value={formData.status} onChange={e => field('status', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                    <option value="available">Available</option>
                    <option value="occupied">Occupied</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
              )}
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Amenities</label>
                <textarea placeholder="ex: electricity, water, WiFi…" value={formData.amenities}
                  onChange={e => field('amenities', e.target.value)} rows={3}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"/>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="owned" checked={formData.is_owned_by_airport}
                  onChange={e => field('is_owned_by_airport', e.target.checked)} className="w-4 h-4 accent-blue-500"/>
                <label htmlFor="owned" className="text-gray-300 text-sm">Airport property</label>
              </div>
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button onClick={closeModal} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm w-full sm:w-auto">Cancel</button>
              <button onClick={handleHangarSubmit} disabled={saving || !formData.surface_sqft}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm w-full sm:w-auto">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          Modal — Edit Lease - Responsive
      ══════════════════════════════════════════════ */}
      {showLeaseEditModal && editingLease && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-md shadow-2xl">
            <div className="p-4 sm:p-5 border-b border-gray-700">
              <h3 className="text-white font-bold text-base sm:text-lg">Edit Lease {editingLease.lease_number}</h3>
              <p className="text-gray-400 text-xs mt-1">
                {editingLease.hangar_number} — {editingLease.tenant_name}
              </p>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Monthly Rent ($)</label>
                  <input type="number" step="0.01" value={leaseEditForm.monthly_rent}
                    onChange={e => setLeaseEditForm(f => ({...f, monthly_rent: e.target.value}))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"/>
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Payment Day</label>
                  <input type="number" min="1" max="28" value={leaseEditForm.payment_day}
                    onChange={e => setLeaseEditForm(f => ({...f, payment_day: e.target.value}))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"/>
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">End Date</label>
                <input type="date" value={leaseEditForm.end_date}
                  onChange={e => setLeaseEditForm(f => ({...f, end_date: e.target.value}))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"/>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="autoRenewEdit" checked={leaseEditForm.is_auto_renew}
                  onChange={e => setLeaseEditForm(f => ({...f, is_auto_renew: e.target.checked}))}
                  className="w-4 h-4 accent-blue-500"/>
                <label htmlFor="autoRenewEdit" className="text-gray-300 text-sm flex items-center gap-1">
                  <RefreshCw size={12} className="sm:w-3 sm:h-3"/> Automatic renewal
                </label>
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Notes</label>
                <textarea value={leaseEditForm.notes}
                  onChange={e => setLeaseEditForm(f => ({...f, notes: e.target.value}))}
                  rows={2} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"/>
              </div>
            </div>
            <div className="p-4 sm:p-5 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button onClick={() => setShowLeaseEditModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm w-full sm:w-auto">Cancel</button>
              <button onClick={handleLeaseEditSubmit} disabled={savingLease}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm w-full sm:w-auto">
                {savingLease ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          Modal — Lease Invoices / Receipts - Responsive
      ══════════════════════════════════════════════ */}
      {showInvoiceModal && selectedLeaseForInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="p-4 sm:p-5 border-b border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sticky top-0 bg-gray-800">
              <div>
                <h3 className="text-white font-bold text-base sm:text-lg">
                  Invoices — {selectedLeaseForInvoice.lease_number}
                </h3>
                <p className="text-gray-400 text-xs mt-1">
                  {selectedLeaseForInvoice.hangar_number} · {selectedLeaseForInvoice.tenant_name}
                  · ${Number(selectedLeaseForInvoice.monthly_rent || selectedLeaseForInvoice.ground_rent || 0).toLocaleString()}/month
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <button onClick={() => handleGenerateInvoice(selectedLeaseForInvoice.id)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-xs flex items-center justify-center gap-1">
                  <Plus size={12} className="sm:w-3 sm:h-3"/> Generate this month's invoice
                </button>
                <button onClick={() => setShowInvoiceModal(false)} className="text-gray-400 hover:text-white">
                  <XCircle size={20} />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-5">
              {loadingInvoices ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-500 mr-2"/>
                  <span className="text-gray-400 text-sm">Loading…</span>
                </div>
              ) : leaseInvoices.length === 0 ? (
                <div className="text-center py-10">
                  <FileText size={32} className="sm:w-10 sm:h-10 text-gray-600 mx-auto mb-3"/>
                  <p className="text-gray-500 text-sm">No invoices for this lease.</p>
                  <button onClick={() => handleGenerateInvoice(selectedLeaseForInvoice.id)}
                    className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm">
                    Generate first invoice
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {leaseInvoices.map((inv) => (
                    <div key={inv.id} className="bg-gray-700/40 rounded-lg border border-gray-600 p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                        <div>
                          <p className="text-white font-mono text-xs sm:text-sm font-semibold">{inv.invoice_number}</p>
                          <p className="text-gray-400 text-xs mt-0.5">
                            {new Date(inv.created_at).toLocaleDateString('en-US')}
                            {inv.due_date && ` · Due: ${new Date(inv.due_date).toLocaleDateString('en-US')}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-white font-bold text-sm sm:text-base">${Number(inv.total_amount).toLocaleString()}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${invoiceStatusStyle(inv.status)}`}>
                            {invoiceStatusLabel(inv.status)}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-3">
                        {inv.status !== 'paid' && (
                          <button onClick={() => handleMarkAsPaid(inv.id)}
                            className="px-2 py-1 sm:px-3 sm:py-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 text-xs flex items-center gap-1">
                            <CheckCircle size={11} className="sm:w-3 sm:h-3"/> Mark paid
                          </button>
                        )}
                        <button onClick={() => handleDownloadPDF(inv)}
                          className="px-2 py-1 sm:px-3 sm:py-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 text-xs flex items-center gap-1">
                          <FileText size={11} className="sm:w-3 sm:h-3"/> PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          Modal — Print Lease - Responsive
      ══════════════════════════════════════════════ */}
      {showPrintModal && printLease && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl w-full max-w-[95%] sm:max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-3 sm:p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <img
                  src={LOGO_URL}
                  alt="Logo"
                  className="h-8 sm:h-10 w-auto object-contain rounded"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <h3 className="text-gray-800 font-bold text-base sm:text-lg">Lease {printLease.lease_number}</h3>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button onClick={handlePrint}
                  className="flex-1 sm:flex-none px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm flex items-center justify-center gap-2">
                  <FileText size={14}/> Print / PDF
                </button>
                <button onClick={() => setShowPrintModal(false)}
                  className="flex-1 sm:flex-none px-3 py-1.5 sm:px-3 sm:py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 text-sm">
                  Close
                </button>
              </div>
            </div>

            {/* Printable content */}
            <div id="lease-print-content" className="p-4 sm:p-8">
              <style dangerouslySetInnerHTML={{
                __html: `
                  @media print {
                    body { margin: 0; padding: 0; }
                    .no-break { page-break-inside: avoid; }
                  }
                  .lease-text {
                    font-family: 'Courier New', Courier, monospace;
                    font-size: 11px;
                    line-height: 1.5;
                    color: #1a1a1a;
                    white-space: pre-wrap;
                  }
                  .lease-text strong, .lease-text b {
                    font-weight: 700;
                    color: #000;
                  }
                `
              }} />
              <div className="lease-text whitespace-pre-wrap font-mono text-xs sm:text-sm text-gray-800">
                {printLease.documents || "No lease terms available."}
              </div>
              
              {/* Signatures */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-12 mt-8 sm:mt-12 pt-4">
                <div className="border-t-2 border-gray-400 pt-2">
                  <p className="text-xs font-semibold text-gray-700">LESSOR (or Authorized Agent)</p>
                  <p className="text-xs text-gray-500 mt-2">Signature: _______________________</p>
                  <p className="text-xs text-gray-500 mt-1">Date: _______________________</p>
                </div>
                <div className="border-t-2 border-gray-400 pt-2">
                  <p className="text-xs font-semibold text-gray-700">LESSEE</p>
                  <p className="text-xs text-gray-500 mt-2">Signature: _______________________</p>
                  <p className="text-xs text-gray-500 mt-1">Date: _______________________</p>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center mt-8 sm:mt-12 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-400">
                  Document generated by AeroTrick FBO, Texas — {new Date().toLocaleDateString('en-US')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Lease Create */}
      <LeaseModal
        isOpen={showLeaseModal}
        onClose={() => { setShowLeaseModal(false); setSelectedHangarForLease(null); }}
        onSuccess={fetchData}
        hangar={selectedHangarForLease}
      />
    </div>
  );
};

export default HangarsView;